import { useEffect } from 'react'

import { AlertTriangle } from 'lucide-react'
import { z } from 'zod'

import { spreadsheetFileTypes } from '@fortem-monorepo/constants'
import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import { DialogProps } from '@fortem-monorepo/react-ui'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { FileUploadInput } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { Alert, AlertDescription, AlertTitle } from '@fortem-monorepo/react-ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@fortem-monorepo/react-ui'
import { Form, FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'

import { useUploadDataMutation } from '@/react-query/mutations/data-upload'

const schema = z
  .object({
    value_date: z.string(),
    liquidity_report: z.instanceof(FileList).optional(),
    rating: z.instanceof(FileList).optional(),
  })
  .superRefine((data, ctx) => {
    const liquidityHasFiles = (data.liquidity_report ?? [])?.length > 0
    const ratingHasFiles = (data.rating ?? [])?.length > 0

    if (!liquidityHasFiles) {
      ctx.addIssue({
        path: ['liquidity_report'],
        code: z.ZodIssueCode.custom,
        message: 'Upload at least one file here or in Fund Rating.',
      })
    }

    if (!ratingHasFiles) {
      ctx.addIssue({
        path: ['rating'],
        code: z.ZodIssueCode.custom,
        message: 'Upload at least one file here or in Fund Liquidity Reporting.',
      })
    }
  })

export interface UploadDataModalProps extends DialogProps {}

export const UploadDataModal: React.FC<UploadDataModalProps> = ({ ...props }) => {
  const uploadDataMutation = useUploadDataMutation()
  const isMutating = uploadDataMutation.isPending

  const form = useFormWithSchema(
    {
      mode: 'all',
      defaultValues: {
        value_date: toISO8601(getTMinus1()),
      },
    },
    schema,
  )

  const { getValues, setValue, register, control, trigger, resetField } = form

  useEffect(() => {
    trigger()
  }, [])

  const submitForm = async (type: 'liquidity_report' | 'rating') => {
    let values = getValues()

    switch (type) {
      case 'liquidity_report':
        values.rating = undefined
        break
      case 'rating':
        values.liquidity_report = undefined
    }

    await uploadDataMutation.mutateAsync(values)
    resetField(type)
    trigger(type)
  }

  return (
    <Form {...form}>
      <Dialog {...props}>
        <DialogContent className={'max-w-175! w-full!'}>
          <DialogHeader>
            <DialogTitle className={'mb-5'}>Upload Data</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-6">
            <FormField
              control={control}
              name="value_date"
              render={({ field: { value } }) => {
                return (
                  <FormItem>
                    <FormLabel>Value Date</FormLabel>
                    <DatePicker
                      selected={value ? new Date(value) : undefined}
                      onSelect={(date) => date && setValue('value_date', toISO8601(date))}
                    />
                  </FormItem>
                )
              }}
            />
            <FormField
              control={control}
              name="liquidity_report"
              render={() => {
                const props = register('liquidity_report')
                return (
                  <FormItem className={'flex-1'}>
                    <FormLabel>Fund Liquidity Reporting</FormLabel>
                    <div className="flex items-center gap-2">
                      <FileUploadInput
                        {...props}
                        accept={spreadsheetFileTypes.join(',')}
                        multiple={false}
                        files={getValues('liquidity_report')!}
                        onChange={(e) => {
                          props.onChange(e)
                          trigger(['liquidity_report', 'rating'])
                        }}
                        setValue={(val) => {
                          setValue('liquidity_report', val, { shouldValidate: true })
                          trigger(['liquidity_report', 'rating'])
                        }}
                      />
                      <LoadingButton
                        className={'w-full h-full flex-0'}
                        disabled={!getValues('liquidity_report')?.length || isMutating}
                        onClick={async (e) => {
                          e.preventDefault()
                          await submitForm('liquidity_report')
                        }}
                      >
                        Submit
                      </LoadingButton>
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              control={control}
              name="rating"
              render={() => {
                const props = register('rating')
                return (
                  <FormItem>
                    <FormLabel>Fund Rating</FormLabel>
                    <div className="flex items-center gap-2">
                      <FileUploadInput
                        {...props}
                        accept={spreadsheetFileTypes.join(',')}
                        files={getValues('rating')!}
                        multiple={false}
                        onChange={(e) => {
                          props.onChange(e)
                          trigger(['liquidity_report', 'rating'])
                        }}
                        setValue={(val) => {
                          setValue('rating', val, { shouldValidate: true })
                          setTimeout(() => trigger(), 100)
                        }}
                      />
                      <LoadingButton
                        className={'w-full h-full flex-0'}
                        disabled={!getValues('rating')?.length || isMutating}
                        onClick={() => submitForm('rating')}
                      >
                        Submit
                      </LoadingButton>
                    </div>
                  </FormItem>
                )
              }}
            />
            <Alert className={'bg-amber-100/50 border-amber-200'}>
              <AlertTriangle className={'text-amber-500!'} />
              <AlertTitle
                className={'text-muted-foreground font-semibold text-[15px] tracking-[0.04px]'}
              >
                Warning
              </AlertTitle>
              <AlertDescription>
                Ensure you have double checked your files, as well as uploading them to the right
                place.
              </AlertDescription>
            </Alert>
          </div>
        </DialogContent>
      </Dialog>
    </Form>
  )
}
